import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Sun, Clock, ChevronRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { QC_CITIES, QC_ON_CITIES, qcCityPath, qcOnCityPath } from "../data/quebec";

export default function QuebecHome() {
  return (
    <div data-testid="qc-home-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title="Services de sécurité au Québec | 1st Nations Security"
        description="1st Nations Security — entreprise de sécurité 100 % autochtone. Agents de sécurité licenciés, patrouille mobile, sécurité événementielle et de chantier au Québec. +1 807-770-0243."
        path="/qc"
        lang="fr-CA"
      />
      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">
        Entreprise 100 % autochtone — Services de sécurité
      </p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        Services de Sécurité au <span className="text-amber-400">Québec</span>
      </h1>
      <div className="max-w-3xl space-y-6">
        <p className="text-base text-slate-300 leading-relaxed">
          1st Nations Security est une entreprise de sécurité détenue à 100 % par des Autochtones, avec des racines
          dans le Nord-Ouest de l'Ontario. Nous offrons des services de gardiennage professionnels en français et en
          anglais partout au Québec et en Ontario : agents de sécurité en uniforme, patrouilles mobiles, sécurité de
          chantiers de construction, sécurité événementielle, prévention des pertes, surveillance incendie et réponse
          aux alarmes.
        </p>
        <p className="text-base text-slate-300 leading-relaxed">
          Notre centre de répartition répond 24 heures sur 24, 7 jours sur 7. Appelez le +1 807-770-0243 ou écrivez à
          admin@1stnation-security.com pour discuter de vos besoins en sécurité — sans formulaire, une vraie personne
          vous répond.
        </p>
      </div>
      <div className="mt-8 flex flex-wrap gap-4">
        <a href="tel:+18077700243" data-testid="qc-hero-call-button" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-200 hover:bg-amber-400 hover:scale-[1.02]">
          Appelez le +1 807-770-0243
        </a>
        <Link to="/qc/services-de-securite" data-testid="qc-hero-services-button" className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-7 py-3.5 text-sm font-semibold text-amber-300 transition-colors duration-200 hover:bg-amber-500/10">
          Nos services de sécurité <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
        {[
          { icon: ShieldCheck, label: "Agents licenciés et bilingues" },
          { icon: Sun, label: "100 % détenue par des Autochtones" },
          { icon: Clock, label: "Répartition 24/7" },
        ].map((b) => (
          <div key={b.label} data-testid={`qc-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
            <b.icon className="h-5 w-5 text-amber-400 shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-slate-200">{b.label}</span>
          </div>
        ))}
      </div>

      <section data-testid="qc-cities-section" className="mt-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          Nos Services de Sécurité par Ville au Québec
        </h2>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-8">
          Nous desservons les grandes villes du Québec. Choisissez votre ville pour découvrir nos services locaux :
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {QC_CITIES.map((c) => (
            <Link
              key={c.slug}
              to={qcCityPath(c)}
              data-testid={`qc-city-link-${c.slug}`}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-slate-950/70 px-3.5 py-2.5 text-sm text-slate-300 transition-colors duration-200 hover:border-amber-500/40 hover:text-amber-300"
            >
              <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" /> {c.name}
            </Link>
          ))}
        </div>
      </section>

      <section data-testid="qc-ontario-section" className="mt-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          Services en Français dans l'Ontario Francophone
        </h2>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-8">
          Nous servons aussi les communautés francophones de l'Ontario — d'Ottawa à Hearst — avec des agents de
          sécurité bilingues :
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {QC_ON_CITIES.map((c) => (
            <Link
              key={c.slug}
              to={qcOnCityPath(c)}
              data-testid={`qc-on-city-link-${c.slug}`}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-slate-950/70 px-3.5 py-2.5 text-sm text-slate-300 transition-colors duration-200 hover:border-amber-500/40 hover:text-amber-300"
            >
              <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" /> {c.name}
            </Link>
          ))}
        </div>
      </section>

      <p className="mt-12 text-sm text-slate-400">
        Vous cherchez nos services en Ontario en anglais?{" "}
        <Link to="/" data-testid="qc-english-home-link" className="text-amber-400 hover:text-amber-300 font-semibold">
          Visit our English site <ChevronRight className="inline h-3.5 w-3.5" />
        </Link>
      </p>

      <CtaBand
        heading="Besoin d'agents de sécurité au Québec?"
        sub="Appelez notre répartition 24/7 au +1 807-770-0243 ou écrivez à admin@1stnation-security.com pour un plan de sécurité adapté à votre site, commerce ou événement."
      />
    </div>
  );
}
